import { getCookie } from 'cookies-next';
import type { IncomingMessage } from 'http';

import { BREAKPOINT_SIZES } from '@/utils/constants/ui';
import { VIEWPORT_WIDTH_COOKIE_NAME } from '@/utils/constants/viewport-client-hints';
import parseCssPx from '@/utils/ui/parseCssPx';

const SEC_CH_VIEWPORT_WIDTH = 'sec-ch-viewport-width';

const parsePositiveInt = (raw: string | undefined): number | undefined => {
    if (raw == null || raw === '') {
        return undefined;
    }
    const n = parseInt(String(raw).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
};

/**
 * Viewport width (CSS px) for SSR.
 * Prefers {@link VIEWPORT_WIDTH_COOKIE_NAME} (set from `window.innerWidth`, aligns with `matchMedia`)
 * when valid; otherwise uses `Sec-CH-Viewport-Width` when present.
 */
const getSsrViewportWidthFromReq = (req: IncomingMessage | undefined): number | undefined => {
    if (!req?.headers) {
        return undefined;
    }
    const { headers } = req;

    const cookieRaw = getCookie(VIEWPORT_WIDTH_COOKIE_NAME, { req });
    const fromCookie = typeof cookieRaw === 'string' ? parsePositiveInt(cookieRaw) : undefined;
    if (fromCookie != null) {
        return fromCookie;
    }

    const hintRaw = headers[SEC_CH_VIEWPORT_WIDTH];
    const hintStr = Array.isArray(hintRaw) ? hintRaw[0] : hintRaw;
    return parsePositiveInt(hintStr);
};

// Canonical image widths derived from VDS breakpoint min values.
// 360 is a common small-phone floor below the first VDS breakpoint boundary.
const CANONICAL_VIEWPORT_WIDTHS: readonly number[] = [
    360,
    parseCssPx(BREAKPOINT_SIZES.MOBILE_LANDSCAPE_MIN), // 480
    parseCssPx(BREAKPOINT_SIZES.TABLET_PORTRAIT_MIN), // 768
    parseCssPx(BREAKPOINT_SIZES.TABLET_LANDSCAPE_MIN), // 1120
    parseCssPx(BREAKPOINT_SIZES.DESKTOP_MIN), // 1440
    parseCssPx(BREAKPOINT_SIZES.DESKTOP_WIDE_MIN), // 1920
]
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

/**
 * Snaps a raw viewport pixel width to the smallest canonical breakpoint width that covers it.
 * Widths are derived from VDS breakpoint min values so they stay in sync with the design system.
 * Useful for generating stable, cache-friendly image URLs on both SSR and client.
 *
 * 360 → 360, 390 → 480, 768 → 768, 1024 → 1120, 1440 → 1440, 1920+ → 1920
 *
 * Falls back to the largest canonical width when viewportPx is absent, non-finite, or too large.
 */
const resolveCanonicalViewportWidth = (viewportPx: number | null | undefined): number => {
    const max = CANONICAL_VIEWPORT_WIDTHS[CANONICAL_VIEWPORT_WIDTHS.length - 1] ?? 1920;
    if (viewportPx == null || !Number.isFinite(viewportPx) || viewportPx <= 0) {
        return max;
    }
    return CANONICAL_VIEWPORT_WIDTHS.find((w) => w >= viewportPx) ?? max;
};

export { getSsrViewportWidthFromReq, resolveCanonicalViewportWidth };
